import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'

const API_BASE = import.meta.env.VITE_API_URL || ''

function MobileQuoteUpload() {
  const { token } = useParams()
  const [session, setSession] = useState(null)
  const [error, setError] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadedCount, setUploadedCount] = useState(0)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [successMessage, setSuccessMessage] = useState(null)
  const [uploadedPhotos, setUploadedPhotos] = useState([])

  useEffect(() => {
    validateSession()
  }, [token])

  async function validateSession() {
    try {
      const res = await fetch(`${API_BASE}/api/testing/quote-dummy/session/${token}/validate`)
      const data = await res.json()
      if (!res.ok) {
        setError(data.error || 'Invalid or expired session')
        return
      }
      setSession(data)
      setUploadedCount(data.uploadCount || 0)
    } catch {
      setError('Unable to connect. Please try again.')
    }
  }

  async function handleFileChange(e) {
    const files = Array.from(e.target.files)
    if (files.length === 0) return

    setUploading(true)
    setSuccessMessage(null)
    let uploaded = 0

    for (const file of files) {
      if (uploadedCount + uploaded >= (session?.maxUploads || 10)) {
        setError(`Upload limit reached (${session?.maxUploads || 10} photos)`)
        break
      }

      const formData = new FormData()
      formData.append('photo', file)

      try {
        setUploadProgress(Math.round(((uploaded) / files.length) * 100))
        const res = await fetch(`${API_BASE}/api/testing/quote-dummy/session/${token}/upload`, {
          method: 'POST',
          body: formData
        })
        const data = await res.json()
        if (res.ok) {
          uploaded++
          setUploadedCount(data.uploadCount)
          if (data.photo) {
            setUploadedPhotos(prev => [...prev, data.photo])
          }
        } else {
          setError(data.error || 'Upload failed')
          break
        }
      } catch {
        setError('Upload failed. Check your connection.')
        break
      }
    }

    setUploadProgress(100)
    setUploading(false)
    if (uploaded > 0) {
      setSuccessMessage(`${uploaded} photo${uploaded !== 1 ? 's' : ''} uploaded successfully`)
    }
    // Reset file input
    e.target.value = ''
  }

  const remaining = session ? session.maxUploads - uploadedCount : 0

  if (error && !session) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.icon}>&#x26A0;&#xFE0F;</div>
          <h1 style={styles.title}>Upload Unavailable</h1>
          <p style={styles.errorText}>{error}</p>
          <p style={styles.hint}>Please scan a new QR code to start a fresh session.</p>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.icon}>&#x1F4F7;</div>
        <h1 style={styles.title}>Upload Your Gear Photos</h1>
        <p style={styles.subtitle}>Sell Your Music Gear — Photo Upload</p>

        {session && (
          <div style={styles.stats}>
            <span>{uploadedCount} uploaded</span>
            <span style={styles.divider}>&bull;</span>
            <span>{remaining} remaining</span>
          </div>
        )}

        {error && <div style={styles.errorBanner}>{error}</div>}
        {successMessage && <div style={styles.successBanner}>{successMessage}</div>}

        {uploading ? (
          <div style={styles.progressContainer}>
            <div style={styles.progressBar}>
              <div style={{ ...styles.progressFill, width: `${uploadProgress}%` }} />
            </div>
            <p style={styles.progressText}>Uploading... {uploadProgress}%</p>
          </div>
        ) : remaining > 0 ? (
          <div style={styles.buttonGroup}>
            <label style={styles.uploadButton}>
              <input
                type="file"
                accept="image/jpeg,image/png"
                multiple
                capture="environment"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              Take Photo
            </label>
            <label style={{ ...styles.uploadButton, background: '#6b7280' }}>
              <input
                type="file"
                accept="image/jpeg,image/png"
                multiple
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              Choose from Gallery
            </label>
          </div>
        ) : (
          <p style={styles.hint}>Upload limit reached. Scan a new QR code if you need to upload more.</p>
        )}

        <p style={styles.hint}>JPG or PNG &bull; Max 20MB per photo</p>

        {uploadedPhotos.length > 0 && (
          <div style={styles.thumbnailGrid}>
            {uploadedPhotos.map(photo => (
              <div key={photo.id} style={styles.thumbnailWrapper}>
                <img
                  src={photo.url}
                  alt={photo.filename}
                  style={styles.thumbnail}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#111827',
    padding: 20,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  card: {
    background: '#1f2937',
    borderRadius: 16,
    padding: '32px 24px',
    textAlign: 'center',
    maxWidth: 400,
    width: '100%',
    color: '#f3f4f6'
  },
  icon: { fontSize: 48, marginBottom: 12 },
  title: { margin: '0 0 4px', fontSize: 22, fontWeight: 700 },
  subtitle: { margin: '0 0 20px', fontSize: 13, color: '#9ca3af' },
  stats: {
    display: 'flex',
    justifyContent: 'center',
    gap: 8,
    fontSize: 14,
    color: '#d1d5db',
    marginBottom: 20,
    padding: '8px 16px',
    background: '#374151',
    borderRadius: 8
  },
  divider: { color: '#6b7280' },
  buttonGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    alignItems: 'center'
  },
  uploadButton: {
    display: 'inline-block',
    padding: '14px 32px',
    background: '#16a34a',
    color: 'white',
    borderRadius: 10,
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    marginBottom: 12
  },
  progressContainer: { marginBottom: 12 },
  progressBar: {
    height: 8,
    background: '#374151',
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 8
  },
  progressFill: {
    height: '100%',
    background: '#16a34a',
    borderRadius: 4,
    transition: 'width 0.3s'
  },
  progressText: { fontSize: 14, color: '#9ca3af' },
  hint: { fontSize: 12, color: '#6b7280', margin: '8px 0 0' },
  errorText: { fontSize: 15, color: '#fbbf24', margin: '8px 0 16px' },
  errorBanner: {
    background: '#7f1d1d',
    color: '#fca5a5',
    padding: '8px 12px',
    borderRadius: 8,
    fontSize: 13,
    marginBottom: 12
  },
  successBanner: {
    background: '#064e3b',
    color: '#6ee7b7',
    padding: '8px 12px',
    borderRadius: 8,
    fontSize: 13,
    marginBottom: 12
  },
  thumbnailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 8,
    marginTop: 16
  },
  thumbnailWrapper: {
    aspectRatio: '1',
    borderRadius: 8,
    overflow: 'hidden',
    background: '#374151'
  },
  thumbnail: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  }
}

export default MobileQuoteUpload
